/* eslint-disable react-hooks/exhaustive-deps */
/*
 * Appeals sub-tab — list pending/all appeals + approve/deny with notes.
 * Approving an appeal automatically unbans the player on every server
 * AND marks them as admin-unbanned (so the auto-detection loop won't
 * re-ban them based on old log lines).
 */
import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { MessageSquare, Check, X, RefreshCw } from "lucide-react";

export default function AppealsTab({ toast, reloadStats }) {
  const [appeals, setAppeals] = useState([]);
  const [status, setStatus] = useState("pending");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/moderation/appeals", { params: status === "all" ? {} : { status } });
      setAppeals(data.appeals || []);
    } catch (e) { toast.error(apiError(e)); }
  }, [status, toast]);

  useEffect(() => { load(); }, [load]);

  const review = async (id, action) => {
    const verb = action === "approve" ? "approve & unban" : "deny";
    const notes = window.prompt(`Notes for ${verb}? (optional, visible to admins)`, "");
    if (notes === null) return; // cancelled
    setBusyId(id);
    try {
      await api.post(`/admin/moderation/appeals/${id}/${action}`, { notes });
      toast.success(action === "approve" ? "Appeal approved — player unbanned" : "Appeal denied");
      load();
      reloadStats?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-4">
      <div className="glass-card p-3 flex items-center gap-2">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="input" data-testid="appeals-status-filter">
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="denied">Denied</option>
          <option value="all">All</option>
        </select>
        <button onClick={load} className="btn btn-ghost"><RefreshCw size={14} /></button>
      </div>

      <div className="glass-card p-4" data-testid="appeals-list">
        {appeals.length === 0 ? (
          <p className="text-[var(--muted)] text-sm">No appeals to show.</p>
        ) : (
          <ul className="space-y-3">
            {appeals.map((a) => (
              <li key={a.id} className="border-l-2 border-rose-400/40 pl-3 py-2" data-testid={`appeal-row-${a.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-[var(--bone)]">{a.player_id}</span>
                    {a.discord_id ? <span className="text-xs text-[var(--muted)]">Discord: <span className="font-mono">{a.discord_id}</span></span> : null}
                    {a.contact ? <span className="text-xs text-[var(--muted)]">Contact: {a.contact}</span> : null}
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                      a.status === "approved" ? "bg-emerald-500/20 text-emerald-300"
                      : a.status === "denied" ? "bg-rose-500/20 text-rose-300"
                      : "bg-amber-500/20 text-amber-300"
                    }`}>{a.status}</span>
                  </div>
                  <span className="text-xs text-[var(--muted)] font-mono">{a.created_at}</span>
                </div>
                <p className="text-sm text-white/85 mt-2 whitespace-pre-wrap">{a.appeal_reason}</p>
                {a.review_notes && (
                  <p className="text-xs text-[var(--muted)] mt-1.5"><strong>Notes:</strong> {a.review_notes}</p>
                )}
                {a.status === "pending" && (
                  <div className="flex gap-2 mt-2">
                    <button disabled={busyId === a.id} onClick={() => review(a.id, "approve")} className="btn btn-teal text-xs" data-testid={`appeal-approve-${a.id}`}>
                      <Check size={12} /> Approve & Unban
                    </button>
                    <button disabled={busyId === a.id} onClick={() => review(a.id, "deny")} className="btn btn-ghost text-rose-300 text-xs" data-testid={`appeal-deny-${a.id}`}>
                      <X size={12} /> Deny
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
